import { CommonModule } from '@angular/common';
import { Component, DestroyRef, NgZone, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { RoleService } from '@app/shared/services/roles.service';
import { WorkflowDraftConfirmationService } from '../workflow-registry-diagnostics/workflow-draft-confirmation.service';
import { EnvironmentSetupService, SeedStatus } from './environment-setup.service';

@Component({
  selector: 'phms-environment-setup', imports: [CommonModule, UiLibraryModule],
  templateUrl: './environment-setup.component.html',
  styles: [':host { display: block; } .setup { padding: 1.5rem; max-width: 70rem; } header, .actions { display: flex; align-items: center; gap: 1rem; justify-content: space-between; } dl { display: flex; flex-wrap: wrap; gap: 1.5rem; } dt { font-weight: 600; } dd { margin: .25rem 0; } table { width: 100%; border-collapse: collapse; margin: 1rem 0; } th, td { text-align: left; padding: .75rem; border-bottom: 1px solid var(--surface-border, #d1d5db); vertical-align: top; } .error { color: var(--red-700, #b91c1c); }'],
})
export class EnvironmentSetupComponent implements OnInit {
  private readonly api = inject(EnvironmentSetupService);
  private readonly confirmation = inject(WorkflowDraftConfirmationService);
  private readonly destroy = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  readonly canSeed = inject(RoleService).hasAnyPermissionSignal('platform_access_admin.seed');
  readonly status = signal<SeedStatus | null>(null);
  readonly loading = signal(false);
  readonly requesting = signal(false);
  readonly confirming = signal(false);
  readonly accessDenied = signal(false);
  readonly error = signal<string | null>(null);
  readonly active = computed(() => ['queued', 'running'].includes(this.status()?.run?.state ?? ''));
  readonly canRun = computed(() => this.canSeed() && !this.accessDenied() && !!this.status()?.available &&
    !this.active() && !this.loading() && !this.requesting() && !this.confirming());
  readonly phases = computed(() => this.status()?.run?.phases ?? this.status()?.phases ?? []);
  readonly enabledPhases = computed(() => this.status()?.phases.filter(phase => phase.state === 'pending').map(phase => phase.name).join(', ') ?? '');
  private poll: ReturnType<typeof setTimeout> | null = null;

  constructor() { this.destroy.onDestroy(() => { if (this.poll !== null) clearTimeout(this.poll); }); }
  ngOnInit(): void { this.refresh(); }

  refresh(): void {
    if (!this.canSeed() || this.loading() || this.destroy.destroyed) return;
    if (this.poll !== null) { clearTimeout(this.poll); this.poll = null; }
    this.loading.set(true);
    this.api.status().pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: status => {
        this.loading.set(false); this.accessDenied.set(false); this.status.set(status); this.error.set(null);
        if (this.active()) this.poll = this.zone.runOutsideAngular(() => setTimeout(() => this.zone.run(() => this.refresh()), 2500));
      },
      error: error => { this.loading.set(false); this.showError(error); },
    });
  }

  async run(): Promise<void> {
    if (!this.canRun()) return;
    const status = this.status()!;
    this.confirming.set(true);
    try {
      const confirmed = await this.confirmation.confirm({ heading: `Run seed for ${status.environment}?`,
        message: 'Run the enabled setup phases to initialize missing data and repair seed-owned records.',
        confirmText: 'Run seed', cancelText: 'Cancel' });
      if (!confirmed || this.destroy.destroyed || !this.canSeed() || this.accessDenied() || !this.status()?.available || this.active()) return;
      this.requesting.set(true); this.error.set(null);
      this.api.start().pipe(takeUntilDestroyed(this.destroy)).subscribe({
        next: run => {
          this.requesting.set(false);
          this.status.update(current => current ? { ...current, run } : current);
          this.refresh();
        },
        error: error => {
          this.requesting.set(false); this.showError(error);
          // An uncertain POST is never retried automatically. Refresh reads its durable receipt.
        },
      });
    } finally { if (!this.destroy.destroyed) this.confirming.set(false); }
  }

  label(state: string): string {
    return ({ queued: 'Queued', pending: 'Waiting', running: 'Running', completed: 'Completed',
      changed: 'Created / repaired', unchanged: 'Already up to date', skipped: 'Skipped', failed: 'Failed' } as Record<string, string>)[state] ?? state;
  }
  private showError(error: unknown): void {
    if (error instanceof HttpErrorResponse && [401, 403].includes(error.status)) {
      this.accessDenied.set(true); this.status.set(null);
      this.error.set('You do not currently have permission to use environment setup.');
      return;
    }
    const body = error instanceof HttpErrorResponse ? error.error as { detail?: unknown; title?: unknown } | null : null;
    this.error.set(typeof body?.detail === 'string' ? body.detail : typeof body?.title === 'string' ? body.title
      : 'The operation could not be confirmed. Refresh status before trying again.');
  }
}
